import React, { memo, useMemo } from 'react'
import { GridTable } from 'src/components/shared/grid/GridTable'
import type { RetryActionJob } from 'src/interfaces/retryConfig'
import { EmptyRetriesHistory } from './EmptyRetriesHistory'
import { buildRetriesHistoryColumns } from './retriesHistoryColumns'

interface Props {
  actionJobs: RetryActionJob[]
  isLoading: boolean
  /** `null` without `schedulers_manage` — the Reschedule column is not rendered. */
  onReschedule: ((job: RetryActionJob) => void) | null
}

const getRowId = (row: RetryActionJob): string => {
  return row.id
}

/**
 * No `paginationModel` is passed, so `GridTable` (`pagination={!!paginationModel}`) turns
 * pagination off entirely — every row renders at once. The endpoint has no sort either.
 *
 * `autoHeight` matters here beyond looks: this grid sits mid-page, and at a fixed height its
 * virtual scroller swallows the wheel across the whole box, so the page stops scrolling whenever
 * the pointer is over the table — with no rows to scroll in exchange. Sizing to the rows removes
 * the inner scroller entirely.
 *
 * The Reschedule row action closes over `onReschedule`, so the columns are built fresh only when
 * that handler changes.
 */
const RetriesHistoryTableComponent = ({ actionJobs, isLoading, onReschedule }: Props) => {
  const columns = useMemo(() => {
    return buildRetriesHistoryColumns(onReschedule)
  }, [onReschedule])

  return (
    <GridTable<RetryActionJob>
      rows={actionJobs}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      isStandalone
      autoHeight
      EmptyElement={EmptyRetriesHistory}
      tableDataTestId="retries-history-table"
      rowDataTestIdPrefix="retries-history"
    />
  )
}

export const RetriesHistoryTable = memo(RetriesHistoryTableComponent)
